import {
  Action,
  Alert,
  ButtonGroup,
  Card,
  ConfirmModal,
  Disclosure,
  Icon,
  Label,
  RelativeDateTime,
  SimpleSummary,
} from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { useState } from 'react';
import { match } from 'ts-pattern';
import env from '~/env';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import { useStoreFeatureFlags } from '~/providers/StoreUserProvider';
import CopyInput from './CopyInput';
import {
  useMcpIdentityGrantDelete,
  useMcpIdentityGrantList,
} from './hooks/useMcpIdentityGrants';

// the MCP server is served at /mcp on its own host
const mcpUrl = `${env.VITE_BASE_MCP_URL}/mcp`;

// the connector name in Claude, so a dev or stage connection is never mistaken for production
const connectorName = {
  dev: 'Corso Dev',
  stage: 'Corso Stage',
  prod: 'Corso',
}[env.VITE_NODE_ENV];

// opens Claude's add-connector dialog with the name and URL filled in (anthropics/claude-ai-mcp#940)
const addToClaudeUrl = `https://claude.ai/customize/connectors?${new URLSearchParams(
  {
    modal: 'add-custom-connector',
    connectorName,
    connectorUrl: mcpUrl,
  },
)}`;

const mcpClients = ['Claude', 'ChatGPT', 'Other'] as const;

function IdentityGrantItem({
  grant,
}: {
  grant: CrewMerchantUi.UserMcpIdentityGrant;
}) {
  const { mutate: deleteGrant, isPending } = useMcpIdentityGrantDelete();
  const [showConfirm, setShowConfirm] = useState(false);
  const name = grant.name ?? 'MCP Client';

  return (
    <>
      <Disclosure
        renderSummary={
          <SimpleSummary>
            <div className="flex flex-1 items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon icon="lockKey" className="text-neutral-500" />
                <span className="font-medium">{name}</span>
              </div>
              <Action
                icon="remove"
                accessibilityLabel={`Revoke ${name}`}
                variant="ghost"
                onClick={() => setShowConfirm(true)}
              />
            </div>
          </SimpleSummary>
        }
      >
        <div className="flex flex-col gap-4 px-6 py-4">
          <div className="flex flex-col gap-1.5">
            <Label>Connected</Label>
            <RelativeDateTime
              dateTime={grant.createdOn}
              className="text-sm text-neutral-500"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Last Used</Label>
            <RelativeDateTime
              dateTime={grant.lastUsedOn}
              className="text-sm text-neutral-500"
            />
          </div>
        </div>
      </Disclosure>

      <ConfirmModal
        show={showConfirm}
        onCancel={() => setShowConfirm(false)}
        onConfirm={() => {
          deleteGrant(grant.id);
          setShowConfirm(false);
        }}
        title="Revoke Connection"
        prompt={`${name} will lose access to Corso on its next request and will ask for approval again if reconnected.`}
        confirmText="Revoke"
        variant="destructive"
        loading={isPending}
      />
    </>
  );
}

export default function McpServerSettings() {
  const { mcpServer } = useStoreFeatureFlags();
  const isCorsoAdmin = useIsCorsoAdmin();
  const [client, setClient] = useState<(typeof mcpClients)[number]>('Claude');
  const { data } = useMcpIdentityGrantList({
    enabled: mcpServer && isCorsoAdmin,
  });

  // internal preview: merchants never see it, even with the flag on
  if (!mcpServer || !isCorsoAdmin) return null;

  const grants = data?.grants ?? [];

  return (
    <>
      <SettingsAnchorCard id="mcp-server">
        <Card.Line as="header">
          <Card.Heading as="h3">MCP Server</Card.Heading>
          <ButtonGroup horizontal>
            {mcpClients.map((option) => (
              <Action
                key={option}
                onClick={() => setClient(option)}
                pressed={client === option}
                accessibilityLabel={`Connect ${option}`}
              >
                {option}
              </Action>
            ))}
          </ButtonGroup>
        </Card.Line>

        {match(client)
          .with('Claude', () => (
            <>
              <Alert heading="Open Claude and follow its steps to connect Corso, then sign in with your Corso account." />
              <div className="flex justify-end">
                <Action
                  icon="externalLink"
                  side="right"
                  variant="primary"
                  to={addToClaudeUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Claude
                </Action>
              </div>
            </>
          ))
          .with('ChatGPT', () => (
            <>
              <Alert heading="Open ChatGPT and follow its steps to add a custom MCP server with this URL, then sign in with your Corso account." />
              <CopyInput label="Server URL" value={mcpUrl} />
              <div className="flex justify-end">
                <Action
                  icon="externalLink"
                  side="right"
                  variant="primary"
                  to="https://chatgpt.com/plugins"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open ChatGPT
                </Action>
              </div>
            </>
          ))
          .with('Other', () => (
            <>
              <Alert heading="Add this URL to your MCP client, then sign in with your Corso account." />
              <CopyInput label="Server URL" value={mcpUrl} />
            </>
          ))
          .exhaustive()}
      </SettingsAnchorCard>

      {grants.length > 0 && (
        <Card>
          <Card.Line as="header">
            <Card.Heading as="h3">MCP Connections</Card.Heading>
          </Card.Line>
          <div className="flex flex-col gap-2">
            {grants.map((grant) => (
              <IdentityGrantItem key={grant.id} grant={grant} />
            ))}
          </div>
        </Card>
      )}
    </>
  );
}
